import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const OperationalAlertsSettingsModal = ({ open, onClose, settings: initialSettings, eventStateOptions, onSave, t }) => {
  const [settings, setSettings] = useState({
    opr_alerts_tbl_elapsed_days: '',
    opr_alerts_is_bursts: '',
    opr_alerts_event_states: []
  });

  // Update settings when modal opens or initialSettings change
  useEffect(() => {
    if (open) {
      // Default event states: 'new', 'viewed', 'workorder' (excluding 'close event')
      const defaultEventStates = eventStateOptions
        .filter(option => ['new', 'viewed', 'workorder'].includes(option.label.toLowerCase()))
        .map(option => option.value);

      setSettings({
        opr_alerts_tbl_elapsed_days: initialSettings?.opr_alerts_tbl_elapsed_days ?? '',
        opr_alerts_is_bursts: initialSettings?.opr_alerts_is_bursts ?? '',
        // opr_alerts_event_states: initialSettings?.opr_alerts_event_states ?? defaultEventStates
      });
    }
  }, [open, initialSettings, eventStateOptions]);

  const handleChange = (field, value) => {
    setSettings({
      ...settings,
      [field]: value
    });
  };

  const handleEventStateToggle = (stateValue) => {
    const currentStates = settings.opr_alerts_event_states || [];
    const newStates = currentStates.includes(stateValue)
      ? currentStates.filter(s => s !== stateValue)
      : [...currentStates, stateValue];
    handleChange('opr_alerts_event_states', newStates);
  };

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  if (!open) return null;

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1300,
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '8px',
      maxWidth: '500px',
      width: '90%',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    },
    header: {
      padding: '20px',
      borderBottom: '1px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      margin: 0,
      fontSize: '20px',
      fontWeight: '500',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      padding: '4px',
      lineHeight: '1',
      color: '#666',
    },
    content: {
      padding: '20px',
    },
    selectField: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    switchContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '20px',
    },
    toggleButton: {
      position: 'relative',
      width: '50px',
      height: '24px',
      backgroundColor: '#ccc',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
    },
    toggleButtonActive: {
      backgroundColor: '#1976d2',
    },
    toggleSlider: {
      position: 'absolute',
      top: '2px',
      left: '2px',
      width: '20px',
      height: '20px',
      backgroundColor: 'white',
      borderRadius: '50%',
      transition: 'transform 0.3s',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    toggleSliderActive: {
      transform: 'translateX(26px)',
    },
    fieldGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      fontSize: '14px',
    },
    footer: {
      padding: '16px 20px',
      borderTop: '1px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
    },
    button: {
      padding: '8px 20px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      backgroundColor: 'white',
    },
    primaryButton: {
      backgroundColor: '#1976d2',
      color: 'white',
      border: 'none',
    },
    checkboxGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginTop: '8px',
    },
    checkboxItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '4px',
      transition: 'background-color 0.2s',
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer',
    },
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>{t('operational_alerts_settings')}</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={modalStyles.content}>
          {/* Days Elapsed Filter */}
          <div style={modalStyles.fieldGroup}>
            <label style={modalStyles.label}>{t('display_duration')}</label>
            <select
              value={settings.opr_alerts_tbl_elapsed_days}
              onChange={(e) => handleChange('opr_alerts_tbl_elapsed_days', parseInt(e.target.value))}
              style={modalStyles.selectField}
            >
              <option value="0">{t('none')}</option>
              <option value="14">{t('2_weeks')}</option>
              <option value="30">{t('1_month')}</option>
              <option value="90">{t('3_months')}</option>
              <option value="180">{t('6_months')}</option>
              <option value="730">{t('2_years')}</option>
            </select>
          </div>

          {/* Burst Alerts Toggle */}
          <div style={modalStyles.switchContainer}>
            <label style={modalStyles.label}>{t('burst_alerts')}</label>
            <div
              style={{
                ...modalStyles.toggleButton,
                ...(settings.opr_alerts_is_bursts === 1 || settings.opr_alerts_is_bursts === '1' ? modalStyles.toggleButtonActive : {})
              }}
              onClick={() => handleChange('opr_alerts_is_bursts', settings.opr_alerts_is_bursts === 1 || settings.opr_alerts_is_bursts === '1' ? 0 : 1)}
            >
              <div
                style={{
                  ...modalStyles.toggleSlider,
                  ...(settings.opr_alerts_is_bursts === 1 || settings.opr_alerts_is_bursts === '1' ? modalStyles.toggleSliderActive : {})
                }}
              />
            </div>
          </div>

          {/* Default Event States Filter */}
          {/* {eventStateOptions?.length > 0 && (
            <div style={modalStyles.fieldGroup}>
              <label style={modalStyles.label}>{t('default_event_states') || 'Default Event States'}</label>
              <div style={modalStyles.checkboxGroup}>
                {eventStateOptions.map((option) => (
                  <div
                    key={option.value}
                    style={modalStyles.checkboxItem}
                    onClick={() => handleEventStateToggle(option.value)}
                  >
                    <input
                      type="checkbox"
                      checked={settings.opr_alerts_event_states?.includes(option.value)}
                      onChange={() => handleEventStateToggle(option.value)}
                      style={modalStyles.checkbox}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )} */}

        </div>

        <div style={modalStyles.footer}>
          <button style={modalStyles.button} onClick={onClose}>
            {t('cancel')}
          </button>
          <button style={{ ...modalStyles.button, ...modalStyles.primaryButton }} onClick={handleSave}>
            {t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

OperationalAlertsSettingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  settings: PropTypes.object,
  eventStateOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  })),
  onSave: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
};

export default OperationalAlertsSettingsModal;